<template>
  <div>
    <p>
      osu! seems to be looking for the reference bpm using a naive approach,
      where it assumes timing points are sorted.
    </p>
    <p>
      If you <b>manually</b> change the order of the Timing Points in the
      <code>.osu</code>, it will change the reference BPM.
    </p>
    <p>The reference BPM is always written in the brackets.</p>

    <p>
      The following shows examples on the behavior of the reference BPMs in
      different situations.
    </p>
    <p>
      <i
        >Note that the end offset is determined by the final note in the map.</i
      >
    </p>

    <div class="row py-3">
      <div class="col-md-4">
        <div class="card shadow bg-light">
          <div class="card-header">Original</div>
          <div class="card-body">
            <div class="card-text">
              <h5 class="card-title">
                <span class="text-secondary small">150 - 600</span> (600)
              </h5>
              <table class="table">
                <tr>
                  <th>OFFSET</th>
                  <th>BPM</th>
                  <th></th>
                </tr>
                <tr>
                  <td>0</td>
                  <td>200</td>
                  <td>Start</td>
                </tr>
                <tr>
                  <td>100</td>
                  <td>300</td>
                </tr>
                <tr>
                  <td>200</td>
                  <td>150</td>
                </tr>
                <tr>
                  <td>300</td>
                  <td>600</td>
                </tr>
                <tr>
                  <td>5000</td>
                  <td>-</td>
                  <td>End</td>
                </tr>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card shadow bg-light">
          <div class="card-header">Swapped 3rd & 4th</div>
          <div class="card-body">
            <div class="card-text">
              <h5 class="card-title">
                <span class="text-secondary small">150 - 300</span> (150)
              </h5>
              <table class="table">
                <tr>
                  <th>OFFSET</th>
                  <th>BPM</th>
                  <th></th>
                </tr>
                <tr>
                  <td>0</td>
                  <td>200</td>
                  <td>Start</td>
                </tr>
                <tr>
                  <td>100</td>
                  <td>300</td>
                </tr>
                <tr class="table-info">
                  <td>300</td>
                  <td>600</td>
                  <td><i class="bi bi-arrow-up-circle"></i></td>
                </tr>
                <tr class="table-info">
                  <td>200</td>
                  <td>150</td>
                  <td><i class="bi bi-arrow-down-circle"></i></td>
                </tr>
                <tr>
                  <td>5000</td>
                  <td>-</td>
                  <td>End</td>
                </tr>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card shadow bg-light">
          <div class="card-header">Swapped 2nd & 4th</div>
          <div class="card-body">
            <div class="card-text">
              <h5 class="card-title">
                <span class="text-secondary small">200 - 300</span> (300)
              </h5>
              <table class="table">
                <tr>
                  <th>OFFSET</th>
                  <th>BPM</th>
                  <th></th>
                </tr>
                <tr>
                  <td>0</td>
                  <td>200</td>
                  <td>Start</td>
                </tr>
                <tr class="table-info">
                  <td>300</td>
                  <td>600</td>
                  <td><i class="bi bi-arrow-up-circle"></i></td>
                </tr>
                <tr>
                  <td>200</td>
                  <td>150</td>
                </tr>
                <tr class="table-info">
                  <td>100</td>
                  <td>300</td>
                  <td><i class="bi bi-arrow-down-circle"></i></td>
                </tr>
                <tr>
                  <td>5000</td>
                  <td>-</td>
                  <td>End</td>
                </tr>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <h1 id="hypothesis">Hypothesis</h1>
    <p>There is an assumption that the timing points are sorted.</p>
    <p>I&#39;m guessing that this is the algorithm used.</p>

    <div class="card">
      <div class="card-header">Example 1</div>
      <div class="card-body">
        <div class="row">
          <div class="col-md-8">
            <div class="card bg-light">
              <div class="card-header">Swapped 2nd & 4th</div>
              <div class="card-body">
                <div class="card-text">
                  <h5 class="card-title">
                    200 - 300 <span class="fw-bold">(300)</span>
                  </h5>
                  <table class="table">
                    <tr>
                      <th>Offset</th>
                      <th>BPM</th>
                      <th>Offset Difference</th>
                    </tr>
                    <tr>
                      <td>0</td>
                      <td>200</td>
                      <td>300</td>
                    </tr>
                    <tr>
                      <td>300</td>
                      <td>600</td>
                      <td>-100</td>
                    </tr>
                    <tr>
                      <td>200</td>
                      <td>150</td>
                      <td>-100</td>
                    </tr>
                    <tr class="table-danger">
                      <td>100</td>
                      <td>300</td>
                      <td>4900</td>
                    </tr>
                    <tr>
                      <td>5000</td>
                      <td>-</td>
                      <td>End</td>
                    </tr>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-4 p-3">
            <p>osu! calculates the pair-wise difference.</p>
            <p>
              The <b>largest offset difference</b> will be the reference bpm, in
              this case, 300 BPM.
            </p>
            <p>Unsure on how the range is determined but it's not important.</p>
          </div>
        </div>
      </div>
    </div>
    <div class="card">
      <div class="card-header">Example 2</div>
      <div class="card-body">
        <div class="row">
          <div class="col-md-8">
            <div class="card bg-light">
              <div class="card-header">Swapped 3rd & 4th</div>
              <div class="card-body">
                <div class="card-text">
                  <h5 class="card-title">
                    150 - 300 <span class="fw-bold">(150)</span>
                  </h5>
                  <table class="table">
                    <tr>
                      <th>Offset</th>
                      <th>BPM</th>
                      <th>Offset Difference</th>
                    </tr>
                    <tr>
                      <td>0</td>
                      <td>200</td>
                      <td>100</td>
                    </tr>
                    <tr>
                      <td>100</td>
                      <td>300</td>
                      <td>200</td>
                    </tr>
                    <tr>
                      <td>300</td>
                      <td>600</td>
                      <td>-100</td>
                    </tr>
                    <tr class="table-danger">
                      <td>200</td>
                      <td>150</td>
                      <td>4800</td>
                    </tr>
                    <tr>
                      <td>5000</td>
                      <td>-</td>
                      <td>End</td>
                    </tr>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-4 p-3">
            <p>
              The <b>largest offset difference</b> is associated with 150 BPM.
            </p>
          </div>
        </div>
      </div>
    </div>

    <h1>Use Case</h1>
    <p>
      If a map that you've planned on using reference bpm <strong>200</strong>,
      but after some time, it changes to <strong>300</strong> or
      <strong>100</strong>. It&#39;s a pain to adjust all SVs to fix it.
    </p>
    <p>
      You can do is manipulate the order of Timing Points to force a specific
      reference bpm by adding a <strong>0 offset BPM Line</strong>.
    </p>

    <h2>Example</h2>
    <div class="col">
      <div class="row py-4">
        <div class="col-md-6">
          <div class="card">
            <div class="card-header">Example .osu</div>
            <div class="card-body font-monospace lh-sm">
              <p>
                [TimingPoints]<br />
                0,100,4,2,0,50,1,0<br />
                1000,300,4,2,0,50,1,0<br />
                2000,100,4,2,0,50,1,0<br />
                9000,300,4,2,0,50,1,0<br />
                10000,600,4,2,0,50,1,0<br /><br />
                [HitObjects]<br />
                ...<br />
                448,192,100000,5,0,0:0:0:0:<br />
              </p>
              The final offset is 100,000ms.
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="card bg-light">
            <div class="card-header">Translated</div>
            <div class="card-body">
              <div class="card-text">
                <h5 class="card-title">
                  <span class="text-secondary small">100 - 600</span> (100)
                </h5>
                <table class="table">
                  <tr>
                    <th>Offset</th>
                    <th>BPM</th>
                    <th>Offset Difference</th>
                  </tr>
                  <tr>
                    <td>0</td>
                    <td>600</td>
                    <td>1000</td>
                  </tr>
                  <tr>
                    <td>1000</td>
                    <td>200</td>
                    <td>1000</td>
                  </tr>
                  <tr>
                    <td>2000</td>
                    <td>600</td>
                    <td>7000</td>
                  </tr>
                  <tr>
                    <td>9000</td>
                    <td>200</td>
                    <td>1000</td>
                  </tr>
                  <tr class="table-danger">
                    <td>10000</td>
                    <td>100</td>
                    <td>90000</td>
                  </tr>
                  <tr>
                    <td>100000</td>
                    <td>-</td>
                    <td>End</td>
                  </tr>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <p>
          Its reference is <strong>100</strong>, let&#39;s say you want to
          change it to <strong>300</strong>.
        </p>
        <p>
          Add a <strong>0 offset BPM line</strong> at the end in the .osu
          <strong>MANUALLY</strong>.
        </p>
      </div>
      <div class="row py-4">
        <div class="col-md-6">
          <div class="card">
            <div class="card-header">Example .osu</div>
            <div class="card-body font-monospace lh-sm">
              <p>
                [TimingPoints]<br />
                0,100,4,2,0,50,1,0<br />
                1000,300,4,2,0,50,1,0<br />
                2000,100,4,2,0,50,1,0<br />
                9000,300,4,2,0,50,1,0<br />
                10000,600,4,2,0,50,1,0<br />
                0,200,4,2,0,50,1,0<br /><br />
                [HitObjects]<br />
                ...<br />
                448,192,100000,5,0,0:0:0:0:<br />
              </p>
              The final offset is 100,000ms.
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="card bg-light">
            <div class="card-header">Translated</div>
            <div class="card-body">
              <div class="card-text">
                <h5 class="card-title">
                  <span class="text-secondary small">100 - 600</span> (300)
                </h5>
                <table class="table">
                  <tr>
                    <th>Offset</th>
                    <th>BPM</th>
                    <th>Offset Difference</th>
                  </tr>
                  <tr>
                    <td>0</td>
                    <td>600</td>
                    <td>1000</td>
                  </tr>
                  <tr>
                    <td>1000</td>
                    <td>200</td>
                    <td>1000</td>
                  </tr>
                  <tr>
                    <td>2000</td>
                    <td>600</td>
                    <td>7000</td>
                  </tr>
                  <tr>
                    <td>9000</td>
                    <td>200</td>
                    <td>1000</td>
                  </tr>
                  <tr>
                    <td>10000</td>
                    <td>100</td>
                    <td>-10000</td>
                  </tr>
                  <tr class="table-danger">
                    <td>0</td>
                    <td>300</td>
                    <td>100000</td>
                  </tr>
                  <tr>
                    <td>100000</td>
                    <td>-</td>
                    <td>End</td>
                  </tr>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <p>
        This will change the reference bpm to <strong>300</strong> as expected.
      </p>
    </div>

    <h1>Caveats</h1>
    <p>There are 2 problems:</p>
    <ol>
      <li>
        If you save the map, osu! will sort itself and it won't work anymore.
      </li>
      <li>If you upload the map, osu! will save itself (back to 1.)</li>
    </ol>
    <p>The solution to (1.) is to just not save or keep moving it back.</p>
    <p>
      The solution to (2.) is to save the map before clicking the final upload
      button.
    </p>
  </div>
</template>

<script>
export default {
  name: "Blog002",
  mounted() {
    return this.$emit("title", "Osu");
  },
};
</script>

<style scoped></style>
